import { snakeCase } from '@replygirl/change-case-object';
import { deleteRequest, getRequest, postRequest, putRequest, patchRequest, patchFormRequest, parseResponse } from './ApiHelpers';
import type { ItemTranslationEdit } from './modules/viewer/store/useTranslationStore';

export const ApiUrls = {
  user: {
    currentUser: '/api/v5/users',
    update: '/api/v5/users/{{id}}',
    signUp: '/api/v4/account',
    signIn: '/api/v4/login',
    socialLoginCallback: '/auth/{{provider}}/callback?web=true',
    searchUsers: '/api/v5/organization/users/search?q={{query}}',
  },
  organization: {
    get: '/api/v5/organizations/{{organizationId}}',
    update: '/api/v5/organizations/{{organizationId}}', // Added for PATCH requests
    inviteMembers: '/api/v5/organizations/{{organizationId}}/invitations',
  },
  stats: {
    get: '/api/v5/items/{{itemId}}/stats',
    update: '/api/v5/items/{{itemId}}/update_stats',
  },
  features: {
    get: '/api/v5/features/web',
  },
  track: {
    create: '/tracking',
    identify: '/tracking/identify',
  },
  notifications: {
    get: '/api/v5/client_notifications/p/{{page}}/{{limit}}',
    update: '/api/v5/client_notifications/{{notificationId}}',
    markAllAsRead: '/api/v5/client_notifications/update_unviewed_notifications',
  },
  view_track: {
    main: '/track',
    view: '/track/view',
  },
  requestContent: {
    list: '/api/v5/request_contents',
  },
  agents: {
    list: '/api/v5/agents',
    get: '/api/v5/agents/{{agentId}}',
    create: '/api/v5/agents',
    update: '/api/v5/agents/{{agentId}}',
    delete: '/api/v5/agents/{{agentId}}',
    rebuild: '/api/v5/agents/{{agentId}}/rebuild',
    regenerateStep: '/api/v5/agents/{{agentId}}/steps/{{sequence}}/regenerate',
    runs: '/api/v5/agents/{{agentId}}/agent_runs',
  },
  item: {
    get: '/api/v5/items/{{itemId}}',
    transcriptions: {
      list: '/api/v5/transcriptions/{{transcriptionId}}',
      create: '/api/v5/transcriptions',
      update: '/api/v5/transcriptions/{{transcriptionId}}',
    },
    videoEdit: '/api/v5/items/{{itemId}}/video_edit',
    thumbnail: '/api/v5/items/{{itemId}}/thumbnail',
    copy: '/api/v5/items/{{itemId}}/copy',
    comments: '/api/v5/comments',
    reactions: '/api/v5/reactions',
    callToActions: 'api/v5/call_to_actions',
    callToActionsLink: 'call_to_actions/{{ctaId}}/click',
    favorite: '/api/v5/items/{{itemId}}/favorite',
    versions: '/api/v5/items/{{itemId}}/versions',
    smartActions: '/api/v5/items/{{itemId}}/smart_actions',
    toDoItems: '/api/v5/items/{{itemId}}/to_dos',
    redaction: '/api/v5/items/{{itemId}}/redaction',
    aiMetadata: '/api/v5/items/{{itemId}}/ai_metadata',
    translations: {
      list: '/api/v5/items/{{itemId}}/translations',
      show: '/api/v5/items/{{itemId}}/translations/{{language}}',
    },
    artifacts: '/api/v5/items/{{itemId}}/artifacts',
    artifact: '/api/v5/items/{{itemId}}/artifacts/{{artifactId}}',
  },
  collections: '/api/v5/collections',
  items: '/api/v5/items',
  campaignModal: {
    list: '/api/v5/campaign_modals?target={{target}}&verify_segments={{verifySegments}}',
    deleteAll: '/api/v5/campaign_modal/all?target={{target}}',
  },
  licenseRequest: {
    list: '/api/v5/organizations/{{organizationId}}/license_requests',
    create: '/api/v5/organizations/{{organizationId}}/license_request',
  },
  plans: {
    list: '/api/v5/plans',
    listNew: '/api/v5/plans?variant=new',
  },
  integrations: {
    list: '/api/v5/integrations',
    apps: '/api/v5/integrations/apps',
    send: '/api/v5/integrations/send_to_integration',
  },
  entitlements: {
    get: '/api/v5/entitlements',
  },
  template: {
    index: '/api/v5/template',
    apply: '/api/v5/template/{{templateId}}/apply',
    default: '/api/v5/template/{{templateId}}/default',
  },
  admin: {
    stopImpersonating: '/admin/users/stop_impersonating',
  },
  recall: {
    calendarConnections: '/api/v5/recall_calendar_connections',
    calendarConnection: '/api/v5/recall_calendar_connections/{{id}}',
  },
  userPreferences: {
    update: '/api/v5/users/preferences',
  },
};

export type CalendarConnectionReturnTo = 'onboarding';

export interface ReactionListItem {
  id: string;
  name: string;
  count: number;
  videoTimes: number[];
}

export const withQueryParams = (url: string, params: Record<string, string>) => {
  const parsedUrl = new URL(url, window.location.origin);

  Object.entries(params).forEach(([key, value]) => parsedUrl.searchParams.set(key, value));

  return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
};

export const Api = {
  user: {
    currentUser: () => getRequest(ApiUrls.user.currentUser),
    signUp: (email: string, password: string, itemId: string | null = null, signedUpFrom: string | null = null, utmContent: string | null = null) => {
      const url = withQueryParams(ApiUrls.user.signUp, {
        utm_source: 'viewer',
        utm_medium: 'nav_button_image',
        utm_content: utmContent ?? `drop:${itemId}`,
        utm_version: 'copernicus',
      });
      return postRequest(url, { email, password, source: 'web:viewer', signed_up_from: signedUpFrom }).catch(async data => {
        if (data.message) {
          return Promise.reject({ error: true, message: data.message });
        }

        if (Array.isArray(data.errors) && data.errors.length > 0) {
          return Promise.reject({ error: true, message: data.errors[0] });
        }

        return Promise.reject(data);
      });
    },
    signIn: (email: string, password: string, itemId: string | null = null) => {
      const url = `${ApiUrls.user.signIn}?utm_source=viewer&utm_medium=nav_button_image&utm_content=drop:${itemId}&utm_version=copernicus`;

      return postRequest(url, { email, password, source: 'web:viewer' }).catch(async data => {
        if (data.message) {
          return Promise.reject({ error: true, message: data.message });
        }

        if (Array.isArray(data.errors) && data.errors.length > 0) {
          return Promise.reject({ error: true, message: data.errors[0] });
        }

        return Promise.reject(data);
      });
    },
    socialLogin: (provider: string, data: any) => postRequest(ApiUrls.user.socialLoginCallback.replace('{{provider}}', provider), data),
    searchUsers: (query: string) => getRequest(ApiUrls.user.searchUsers.replace('{{query}}', query)),
  },
  notifications: {
    get: (page: number = 1, limit: number = 10) => {
      const url = ApiUrls.notifications.get.replace('{{page}}', String(page)).replace('{{limit}}', String(limit));

      return getRequest(url).then(res => ({
        items: res?.data?.clientNotifications,
        total: res?.data?.totalCount,
        unreadCount: res?.meta?.unread,
      }));
    },
    markAllAsRead: (before: Date) => {
      return patchRequest(`${ApiUrls.notifications.markAllAsRead}`, {
        created_before: before.toISOString(),
      });
    },
    markAsRead: (id: string) => {
      return patchRequest(ApiUrls.notifications.update.replace('{{notificationId}}', id), {
        viewed: true,
        campagin_source: 'web',
      });
    },
  },
  organization: {
    get: (organizationId: string) => getRequest(ApiUrls.organization.get.replace('{{organizationId}}', organizationId)),
    update: (organizationId: string, payload: any) => patchRequest(ApiUrls.organization.update.replace('{{organizationId}}', organizationId), payload),
    inviteMembers: (organizationId: string, data: string[] | Array<{ email: string; role?: string }>, source = '') => {
      const url = ApiUrls.organization.inviteMembers.replace('{{organizationId}}', organizationId);
      const body = data.length > 0 && typeof data[0] === 'object' ? { invitations: data, source } : { emails: data, source };

      return postRequest(url, body, false);
    },
  },
  stats: {
    get: (itemId: string) => getRequest(ApiUrls.stats.get.replace('{{itemId}}', itemId)),
    update: (itemId: string, payload: any) => putRequest(ApiUrls.stats.update.replace('{{itemId}}', itemId), payload),
  },
  requestContent: {
    list: (params?: { page?: number; perPage?: number }) => {
      const url = new URL(ApiUrls.requestContent.list, window.location.origin);
      if (params?.page !== undefined) url.searchParams.set('page', params.page.toString());
      if (params?.perPage !== undefined) url.searchParams.set('per_page', params.perPage.toString());
      return getRequest(url.pathname + url.search);
    },
    get: (id: string) => getRequest(`${ApiUrls.requestContent.list}/${id}`),
    create: (data: any) => postRequest(ApiUrls.requestContent.list, snakeCase(data)),
    update: (id: string, data: any) => patchRequest(`${ApiUrls.requestContent.list}/${id}`, snakeCase(data)),
    delete: (id: string) => deleteRequest(`${ApiUrls.requestContent.list}/${id}`),
    checkCustomUrl: (data: any) => {
      return postRequest(`${ApiUrls.requestContent.list}/${data.id}/check_url`, snakeCase(data));
    },
  },
  features: {
    list: (): Promise<Record<string, unknown>> => getRequest(ApiUrls.features.get).then(res => res.data.features.attributes),
  },
  track: {
    create: (event: string, properties: any = {}, asBeacon?: false): Promise<any> => {
      if (asBeacon && navigator.sendBeacon) {
        return Promise.resolve(navigator.sendBeacon(ApiUrls.track.create, JSON.stringify(snakeCase({ event, properties }))));
      }

      return postRequest(ApiUrls.track.create, { event, properties });
    },
    identify: (traits: Record<string, unknown> = {}): Promise<any> => {
      return postRequest(ApiUrls.track.identify, { traits });
    },
  },
  integrations: {
    list: (forceFetch: boolean = false) => getRequest(forceFetch ? `${ApiUrls.integrations.list}?forceRefresh=true` : ApiUrls.integrations.list),
    apps: () => getRequest(ApiUrls.integrations.apps),
    send: (integration: string, id: string, toDoItemId?: string) =>
      postRequest(ApiUrls.integrations.send, toDoItemId ? { integration, id, toDoItemId } : { integration, id }),
  },
  entitlements: {
    get: (forceRefresh: boolean = false) => getRequest(forceRefresh ? `${ApiUrls.entitlements.get}?forceRefresh=true` : ApiUrls.entitlements.get),
  },
  viewTrack: {
    show: (host: string) => getRequest(new URL(ApiUrls.view_track.main, host).href),
    disallow: (host: string, params: any = {}) => postRequest(new URL(ApiUrls.view_track.main, host).href, params),
    allow: (host: string) => deleteRequest(new URL(ApiUrls.view_track.main, host).href),
    view: (host: string, params: any) => postRequest(new URL(ApiUrls.view_track.view, host).href, params),
  },
  collections: {
    create: (name: string) => postRequest(ApiUrls.collections, { name }),
    list: (page: number = 1, pageSize: number = 20) => getRequest(`${ApiUrls.collections}?page=${page}&per_page=${pageSize}&sort=name`),
    addItem: (itemId: string, collectionId: string) => postRequest(`${ApiUrls.collections}/${collectionId}/items/${itemId}`, {}),
    removeItem: (itemId: string, collectionId: string) => deleteRequest(`${ApiUrls.collections}/${collectionId}/items/${itemId}`, {}),
  },
  item: {
    get: (itemId: string) => getRequest(ApiUrls.item.get.replace('{{itemId}}', itemId)),
    save: (itemId: string, data: Object) => patchRequest(`${ApiUrls.items}/${itemId}`, data),
    complete: (itemId: string, data: Object) => putRequest(`${ApiUrls.items}/${itemId}/completed`, data),
    delete: (itemId: string) => deleteRequest(`${ApiUrls.items}/${itemId}`),
    copy: (itemId: string) => postRequest(ApiUrls.item.copy.replace('{{itemId}}', itemId), {}),
    transcription: {
      list: (transcriptionId: string) => getRequest(ApiUrls.item.transcriptions.list.replace('{{transcriptionId}}', transcriptionId)).then(d => d.data),
      create: (itemId: string, force?: boolean) => postRequest(ApiUrls.item.transcriptions.create, { itemId, force }),
      update: (transcriptionId: string, payload: Object) =>
        patchRequest(ApiUrls.item.transcriptions.update.replace('{{transcriptionId}}', transcriptionId), payload).then(d => d.data),
    },
    aiMetadata: {
      get: (itemId: string) => getRequest(ApiUrls.item.aiMetadata.replace('{{itemId}}', itemId)),
      patch: (itemId: string) => patchRequest(`${ApiUrls.item.aiMetadata.replace('{{itemId}}', itemId)}/use_summary`, {}),
      updateSkipSettings: (itemId: string, settings: { skipFillerWords?: boolean; skipSilences?: boolean }) =>
        patchRequest(ApiUrls.item.aiMetadata.replace('{{itemId}}', itemId), settings),
    },
    translations: {
      list: (itemId: string) => getRequest(ApiUrls.item.translations.list.replace('{{itemId}}', itemId)),
      get: (itemId: string, language: string) => getRequest(ApiUrls.item.translations.show.replace('{{itemId}}', itemId).replace('{{language}}', language)),
      create: (itemId: string, language: string) => postRequest(ApiUrls.item.translations.list.replace('{{itemId}}', itemId), { language }),
      update: (itemId: string, language: string, payload: ItemTranslationEdit) =>
        patchRequest(ApiUrls.item.translations.show.replace('{{itemId}}', itemId).replace('{{language}}', language), payload),
    },
    artifacts: {
      list: (itemId: string) => getRequest(ApiUrls.item.artifacts.replace('{{itemId}}', itemId)),
      get: (itemId: string, artifactId: string) => getRequest(ApiUrls.item.artifact.replace('{{itemId}}', itemId).replace('{{artifactId}}', artifactId)),
    },
    redaction: {
      create: (itemId: string) => postRequest(ApiUrls.item.redaction.replace('{{itemId}}', itemId), {}),
    },
    smartActions: {
      create: (itemId: string, type: string, language?: string) =>
        postRequest(ApiUrls.item.smartActions.replace('{{itemId}}', itemId), language ? { type, language } : { type }),
      get: (itemId: string) => getRequest(ApiUrls.item.smartActions.replace('{{itemId}}', itemId)),
      category: (itemId: string) => getRequest(`${ApiUrls.item.smartActions.replace('{{itemId}}', itemId)}/category`),
    },
    toDoItems: {
      list: (itemId: string) => getRequest(ApiUrls.item.toDoItems.replace('{{itemId}}', itemId)),
      update: (itemId: string, id: string, status: string) =>
        patchRequest(`${ApiUrls.item.toDoItems.replace('{{itemId}}', itemId)}/${id}`, { toDoItem: { status } }),
    },
    comments: {
      get: (commentableType: string, commentableId: string) =>
        getRequest(`${ApiUrls.item.comments}?commentable_type=${commentableType}&commentable_id=${commentableId}`),
      post: (payload: Object) =>
        postRequest(`${ApiUrls.item.comments}`, payload).catch(async resp => {
          const data = await resp.json();
          if (data.message) {
            return Promise.reject({ error: true, message: data.message });
          }
          if (data.errors) {
            return Promise.reject({ error: true, message: data.errors.map((err: { detail: string; title: string }) => err.detail) });
          }

          return Promise.reject(resp);
        }),
      update: (id: string, payload: any) => putRequest(`${ApiUrls.item.comments}/${id}`, payload),
      remove: (id: string) => deleteRequest(`${ApiUrls.item.comments}/${id}`),
    },
    reactions: {
      add: (name: string, id: string, videoTime?: number) =>
        postRequest(`${ApiUrls.item.reactions}`, {
          name,
          reactableId: id,
          reactableType: 'Item',
          ...(videoTime !== undefined ? { videoTime } : {}),
        }).then(
          (res): ReactionListItem => ({
            name: res.data.reaction.attributes.name,
            count: res.data.reaction.attributes.count,
            id: res.data.reaction.id,
            videoTimes: res.data.reaction.attributes.videoTimes,
          })
        ),
      remove: (id: string) =>
        deleteRequest(`${ApiUrls.item.reactions}/${id}`).then(
          (res): ReactionListItem => ({
            name: res.data.reaction.attributes.name,
            count: res.data.reaction.attributes.count,
            id: res.data.reaction.id,
            videoTimes: res.data.reaction.attributes.videoTimes,
          })
        ),
      list: (itemId: string) =>
        getRequest(withQueryParams(ApiUrls.item.reactions, { reactable_type: 'Item', reactable_id: itemId })).then((res): ReactionListItem[] =>
          (res.data.reactions ?? []).map((r: any) => ({
            name: r.attributes.name,
            count: r.attributes.count,
            id: r.id,
            videoTimes: r.attributes.videoTimes,
          }))
        ),
    },
    callToActions: {
      get: (itemId: string) => getRequest(`${ApiUrls.item.callToActions}/${itemId}`).then(res => res.data?.callToAction),
      post: (data: any) => postRequest(`${ApiUrls.item.callToActions}`, data).then(res => res.data?.callToAction),
      link: (ctaId: string) => `${ApiUrls.item.callToActionsLink.replace('{{ctaId}}', ctaId)}`,
      last: () => getRequest(`${ApiUrls.item.callToActions}/last_settings`).then(res => res.data?.callToAction),
    },
    favorite: {
      put: (itemId: string) => putRequest(ApiUrls.item.favorite.replace('{{itemId}}', itemId), {}),
    },
    versions: {
      list: (itemId: string) => getRequest(ApiUrls.item.versions.replace('{{itemId}}', itemId)),
      restore: (itemId: string, versionId: string, asNew: boolean) =>
        postRequest(`${ApiUrls.item.versions.replace('{{itemId}}', itemId)}/${versionId}/restore`, { itemId, versionId, asNew }),
    },
    videoEdit: {
      validate: (itemId: string, payload: Object) => postRequest(`${ApiUrls.item.videoEdit.replace('{{itemId}}', itemId)}/validate`, { payload }),
      merge: (itemId: string, payload: Object) => putRequest(`${ApiUrls.item.videoEdit.replace('{{itemId}}', itemId)}`, { payload }),
    },
    uploadThumbnail: (itemId: string, payload: { file: File }) => {
      const url = ApiUrls.item.thumbnail.replace('{{itemId}}', itemId);
      const formData = new FormData();
      formData.append('file', payload.file);
      return patchFormRequest(url, formData);
    },
    thumbnailPreviews: (itemId: string, timestamps: number[], size?: { width: number; height: number }): Promise<{ timestamp: number; url: string }[]> => {
      const url = new URL(`${ApiUrls.item.thumbnail.replace('{{itemId}}', itemId)}/previews`, window.location.origin);
      timestamps.forEach(timestamp => url.searchParams.append('timestamps[]', String(timestamp)));
      if (size) {
        url.searchParams.set('width', String(size.width));
        url.searchParams.set('height', String(size.height));
      }
      return getRequest(url.pathname + url.search).then(res => res.data.previews.attributes);
    },
  },
  campaignModal: {
    list: (target: string, verifySegments: boolean) =>
      getRequest(ApiUrls.campaignModal.list.replace('{{target}}', target).replace('{{verifySegments}}', verifySegments ? 'true' : 'false')),
    deleteAll: (target: string) => deleteRequest(ApiUrls.campaignModal.deleteAll.replace('{{target}}', target)),
  },
  licenseRequest: {
    list: (organizationId: string) => getRequest(ApiUrls.licenseRequest.list.replace('{{organizationId}}', organizationId)),
    create: (organizationId: string) => postRequest(ApiUrls.licenseRequest.create.replace('{{organizationId}}', organizationId), {}),
  },
  plans: {
    list: () => getRequest(ApiUrls.plans.list),
    listNew: () => getRequest(ApiUrls.plans.listNew),
  },
  template: {
    index: (params?: { slot?: string; id?: string }) => {
      let url = ApiUrls.template.index;
      if (params?.slot) url += `?slot=${params.slot}`;
      return getRequest(url);
    },
    save: (templateData: any) => postRequest(ApiUrls.template.index, templateData),
    apply: (templateId: string, itemId: string) => postRequest(ApiUrls.template.apply.replace('{{templateId}}', templateId), { item: itemId }),
    setDefault: (templateId: string) => postRequest(ApiUrls.template.default.replace('{{templateId}}', templateId), {}),
    removeDefault: (templateId: string) => deleteRequest(ApiUrls.template.default.replace('{{templateId}}', templateId)),
  },
  admin: {
    stopImpersonating: () => deleteRequest(ApiUrls.admin.stopImpersonating),
  },
  agents: {
    list: (params?: { itemId?: string; page?: number; perPage?: number }) => {
      const url = new URL(ApiUrls.agents.list, window.location.origin);
      if (params?.itemId) {
        url.searchParams.set('item_id', params.itemId);
      }
      if (params?.page !== undefined) url.searchParams.set('page', params.page.toString());
      if (params?.perPage !== undefined) url.searchParams.set('per_page', params.perPage.toString());
      return getRequest(url.pathname + url.search);
    },
    get: (agentId: string, params?: { includeDefinition?: boolean }) => {
      const url = new URL(ApiUrls.agents.get.replace('{{agentId}}', agentId), window.location.origin);
      if (params?.includeDefinition) {
        url.searchParams.set('include_definition', 'true');
      }
      return getRequest(url.pathname + url.search);
    },
    create: (data: { itemId: string; title: string }) => postRequest(ApiUrls.agents.create, snakeCase(data)),
    update: (agentId: string, data: { title: string }) => patchRequest(ApiUrls.agents.update.replace('{{agentId}}', agentId), snakeCase(data)),
    delete: (agentId: string) => deleteRequest(ApiUrls.agents.delete.replace('{{agentId}}', agentId)),
    rebuild: (agentId: string) => postRequest(ApiUrls.agents.rebuild.replace('{{agentId}}', agentId), {}),
    regenerateStep: (agentId: string, sequence: number, data: { instruction: string }) =>
      postRequest(ApiUrls.agents.regenerateStep.replace('{{agentId}}', agentId).replace('{{sequence}}', String(sequence)), snakeCase(data)),
    runs: (agentId: string, params?: { page?: number; perPage?: number }) => {
      const url = new URL(ApiUrls.agents.runs.replace('{{agentId}}', agentId), window.location.origin);
      if (params?.page !== undefined) url.searchParams.set('page', params.page.toString());
      if (params?.perPage !== undefined) url.searchParams.set('per_page', params.perPage.toString());
      return getRequest(url.pathname + url.search);
    },
    createRun: (agentId: string, data: Record<string, unknown>) => postRequest(ApiUrls.agents.runs.replace('{{agentId}}', agentId), snakeCase(data)),
  },
  embed: (oembedUrl: string, shareUrl: string) => fetch(`${oembedUrl}?format=json&url=${shareUrl}`).then((response: Response) => parseResponse(response)),
  recall: {
    calendar: {
      list: () => getRequest(ApiUrls.recall.calendarConnections),
      create: (provider: 'google' | 'outlook', returnTo?: CalendarConnectionReturnTo) =>
        postRequest(ApiUrls.recall.calendarConnections, { provider, ...(returnTo ? { returnTo } : {}) }),
      disconnect: (id: number) => deleteRequest(ApiUrls.recall.calendarConnection.replace('{{id}}', String(id))),
    },
  },
  userPreferences: {
    updateMeetings: (meetings: Record<string, unknown>) => patchRequest(ApiUrls.userPreferences.update, { meetings: snakeCase(meetings) }),
    updateDefaultAgent: (defaultAgent: string) => patchRequest(ApiUrls.userPreferences.update, { default_agent: defaultAgent }),
  },
};
